import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Check, X, Trash2, Users, ShieldCheck, ShieldAlert, PowerOff, Power } from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { BRAND_MONOGRAM } from "@/lib/brand";

export default function UsersAdminPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/users");
      setUsers(r.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Apenas o responsável pode acessar.");
        nav("/");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const approve = async (id) => {
    await api.post(`/admin/users/${id}/approve`);
    toast.success("Usuário aprovado.");
    load();
  };
  const revoke = async (id) => {
    await api.post(`/admin/users/${id}/revoke`);
    toast.message("Acesso revogado.");
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Excluir permanentemente este usuário?")) return;
    await api.delete(`/admin/users/${id}`);
    toast.success("Usuário removido.");
    load();
  };
  const deactivate = async (id) => {
    if (!window.confirm("Desativar este usuário? Ele não poderá entrar até ser reativado.")) return;
    await api.post(`/admin/users/${id}/deactivate`);
    toast.message("Usuário desativado.");
    load();
  };
  const activate = async (id) => {
    await api.post(`/admin/users/${id}/activate`);
    toast.success("Usuário reativado.");
    load();
  };

  const pending = users.filter((u) => !u.approved);
  const approved = users.filter((u) => u.approved);

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost" size="sm" data-testid="back-to-calendar"
              onClick={() => nav("/")} className="rounded-sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Calendário
            </Button>
            <img
              src={BRAND_MONOGRAM}
              alt="PodiUmScript" className="h-9 w-auto"
            />
            <div className="border-l border-zinc-200 pl-4">
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-zinc-500 flex items-center gap-1.5">
                <Users className="w-3 h-3" /> Admin
              </div>
              <div className="font-heading text-lg font-bold tracking-tight">Usuários</div>
            </div>
          </div>
          <div className="text-sm text-zinc-600 hidden md:inline">{user?.name || user?.email}</div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-8 space-y-8">
        <section>
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-zinc-500 flex items-center gap-2">
              <ShieldAlert className="w-3 h-3 text-amber-600" /> Pendentes de aprovação
            </div>
            <span className="text-xs text-zinc-500">{pending.length}</span>
          </div>
          {loading ? (
            <div className="text-sm text-zinc-500">Carregando...</div>
          ) : pending.length === 0 ? (
            <Card className="rounded-sm border-dashed border-zinc-300 shadow-none p-8 text-center text-sm text-zinc-500">
              Nenhum cadastro pendente.
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {pending.map((u) => (
                <Card key={u.id} data-testid={`user-pending-${u.id}`}
                  className="rounded-sm border-amber-300 bg-amber-50/40 shadow-none p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-heading text-lg font-bold tracking-tight">{u.name || "(sem nome)"}</div>
                      <div className="text-xs text-zinc-600 mt-0.5">{u.email}</div>
                      {u.created_at && (
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-2">
                          Cadastrado em {format(parseISO(u.created_at), "PPP", { locale: ptBR })}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <Button
                      size="sm"
                      data-testid={`approve-user-${u.id}`}
                      onClick={() => approve(u.id)}
                      className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                    >
                      <Check className="w-3.5 h-3.5 mr-1" /> Aprovar
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      data-testid={`delete-user-${u.id}`}
                      onClick={() => remove(u.id)}
                      className="rounded-sm h-8 border-zinc-300 text-zinc-600 hover:text-red-600 hover:border-red-300"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Recusar
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>

        <section>
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-zinc-500 flex items-center gap-2">
              <ShieldCheck className="w-3 h-3 text-emerald-600" /> Usuários com acesso
            </div>
            <span className="text-xs text-zinc-500">{approved.length}</span>
          </div>
          <div className="border border-zinc-200 rounded-sm bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                <tr>
                  <th className="text-left px-4 py-2 font-bold">Nome</th>
                  <th className="text-left px-4 py-2 font-bold">Email</th>
                  <th className="text-left px-4 py-2 font-bold">Papel</th>
                  <th className="text-right px-4 py-2 font-bold">Ações</th>
                </tr>
              </thead>
              <tbody>
                {approved.map((u) => (
                  <tr key={u.id} data-testid={`user-approved-${u.id}`} className="border-t border-zinc-100">
                    <td className="px-4 py-3 font-semibold text-zinc-900">
                      {u.name || "—"}
                      {u.active === false && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-zinc-200 text-zinc-700 font-bold">
                          Desativado
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-zinc-600">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 font-bold ${u.role === "admin" ? "bg-[#152D4B] text-white" : "bg-zinc-200 text-zinc-700"}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {u.id === user?.id ? (
                        <span className="text-[10px] uppercase tracking-wider text-zinc-400">você</span>
                      ) : (
                        <div className="flex justify-end gap-3">
                          {u.active === false ? (
                            <button
                              data-testid={`activate-user-${u.id}`}
                              onClick={() => activate(u.id)}
                              className="text-xs text-zinc-500 hover:text-emerald-700 flex items-center gap-1"
                            >
                              <Power className="w-3 h-3" /> Reativar
                            </button>
                          ) : (
                            <button
                              data-testid={`deactivate-user-${u.id}`}
                              onClick={() => deactivate(u.id)}
                              className="text-xs text-zinc-500 hover:text-amber-700 flex items-center gap-1"
                            >
                              <PowerOff className="w-3 h-3" /> Desativar
                            </button>
                          )}
                          <button
                            data-testid={`revoke-user-${u.id}`}
                            onClick={() => revoke(u.id)}
                            className="text-xs text-zinc-500 hover:text-amber-700 flex items-center gap-1"
                          >
                            <X className="w-3 h-3" /> Revogar
                          </button>
                          <button
                            data-testid={`delete-approved-${u.id}`}
                            onClick={() => remove(u.id)}
                            className="text-xs text-zinc-400 hover:text-red-600 flex items-center gap-1"
                          >
                            <Trash2 className="w-3 h-3" /> Excluir
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {approved.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-sm text-zinc-500">
                      Nenhum usuário aprovado ainda.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
